import React, {useState, useEffect} from "react"

import authentication from "@irati/auth"
import styled from "styled-components"
import {H3} from "@irati/ui/src/Typography/Heading"
import tracking, {LaunchRecognitionProps} from "@irati-tracking/recognition/recognition"
import walletService from "@irati/wallet/graph/wallet"
import Validation from "@irati/utils/validation"
import getSharingOptions, {PRIVATE_OPTION, TEAMS} from "@irati/recognition/constants/share"
import CreateRecognitionMutation, {NewRecognition} from "@irati/recognition/apollo/mutation/Recognition/Create"
import {FormattedMessage} from "@irati/utils/lang"
import RecognitionService from "@irati/recognition/services/Recognition"
import parseScheduledRecognition from "@irati/recognition/utils/parseScheduledRecognition"
import Loading from "@irati/ui/src/Loading"
import mfaService from "@irati/auth/services/mfa"
import {Share} from "@irati-web/fields/poll"
import isShareableWithTeams from "@irati/recognition/utils/isShareableWithTeams"
import Message, {OnChangeProps} from "@irati-web/fields/recognition/Message"
import UserSearch from "@irati/ui/src/UserSearch"
import getId from "@irati/utils/id"
import {injectIntl, InjectIntlProps} from "@irati/utils/lang/injectIntl"
import Form from "@irati/ui/src/Form"
import {
  FeedEntityType,
  PaymentCurrencyType,
  Recognition,
  SchedulerEvent,
  WalletEntityType,
  RecognitionMessageTemplate,
  SchedulerEventPublishRecognition
} from "@irati/types"
import Spacing from "@irati/ui/src/Spacing"
import Flex from "@irati/ui/src/Flex"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import mainTheme from "@irati/ui/src/global/theme"
import YouNeedToMfa from "@irati-web/mfa/components/Error/YouNeedToMfa"
import {ValidationButton} from "@irati/ui/src/FormItem"
import {useBudgetsList} from "@irati/wallet/hooks/budget"
import MAXIMUM_REWARD_RECIPIENTS from "@irati/reward/constants/maximumRewardRecipients"
import Error from "@irati/ui/src/Loading/Error"
import CTA from "../shared/CTA"
import RewardRecognitionBanner, {RewardInfo} from "../RewardRecognition/RewardRecognitionBanner"
import attachRewardToRecognition from "../../../utils/feed/attachReward"
import {parseRewardError} from "../RewardRecognition/helpers"
import Scheduler from "./Scheduler"
import {FirstRecognitionHeaderBody} from "./styled"
import {Body} from "@irati/ui/src/Typography"

interface Props extends InjectIntlProps {
  selectScreen: (modalToShow?: string) => void
  onRecognitionChange: (recognition: Recognition) => void
  onRecognitionCreated: (props: LaunchRecognitionProps) => void
  onProcessComplete: (recognition: Recognition, isScheduled?: boolean) => void
  onErrorReward: () => void
  recognition: NewRecognition
  scheduledRecognition?: SchedulerEvent
  isFirstRecognition?: boolean
}
const StyledForm = styled(Form)`
  margin: ${mainTheme.spacing.three} 0;
  textarea {
    resize: vertical;
    min-height: 40px;
  }

  .Select-control > *:last-child {
    padding-right: 10px;
  }
`
const getDefaultBudgetId = (scheduledRecognition?: SchedulerEventPublishRecognition, unassignedBudgetId?: string) => {
  if (!scheduledRecognition) {
    return null
  }
  if (!scheduledRecognition.budget?.categoryId) {
    return unassignedBudgetId
  }
  return scheduledRecognition.budget?.categoryId
}
const CreateRecognition: React.FC<Props> = (props) => {
  const {
    selectScreen,
    formatMessage,
    onRecognitionChange,
    onRecognitionCreated,
    onProcessComplete,
    recognition,
    onErrorReward,
    scheduledRecognition,
    intl,
    isFirstRecognition
  } = props
  const {data, refetch: refetchBudgets} = useBudgetsList()
  const [mfaLoad, setMfaLoad] = useState<Promise<any>>()
  const [error, setError] = useState<string>()
  const [selectedMessageTemplate, setSelectedMessageTemplate] = useState<{template: RecognitionMessageTemplate | null; isEdited: boolean}>(
    {
      template: null,
      isEdited: false
    }
  )
  const [rewardInfo, setRewardInfo] = useState<RewardInfo>({
    pointsPerUser: scheduledRecognition?.payload?.publishRecognition?.rewardAmount,
    budgetId: null,
    useUnassigned: !!scheduledRecognition?.payload?.publishRecognition?.budget?.useUnassigned,
    customRewardId: scheduledRecognition?.payload?.publishRecognition?.customRewardId || null
  })
  const [steps, setSteps] = useState({recognition: false, reward: false})
  const [submit, setSubmit] = useState<Promise<any>>()
  const [recognitionCreated, setRecognitionCreated] = useState<Recognition>()
  const [isScheduledRecognition, setIsScheduledRecognition] = useState(!!scheduledRecognition)
  const [scheduledOptions, setScheduledOptions] = useState({
    date: scheduledRecognition?.when || "",
    timezone: scheduledRecognition?.originalTimezone || ""
  })
  const canReward = authentication.apps("wallet.flexiRewards.canRewardEmployees")
  const canShareWithEveryone = authentication.apps("recognition.recognition.everyone")
  const user = authentication.user()
  const canBeSharedWithTeams = isShareableWithTeams(user, recognition)
  const sharingOptions = getSharingOptions(formatMessage, canShareWithEveryone, canBeSharedWithTeams)
  useEffect(() => {
    if (!data) return
    const defaultBudgetId = getDefaultBudgetId(scheduledRecognition?.payload?.publishRecognition, data?.budgets.unassigned.uuid)
    setRewardInfo({
      ...rewardInfo,
      budgetId: defaultBudgetId
    })
  }, [data])
  const handleCancel = () => {
    tracking.cancelRecognition()
    selectScreen()
  }
  const handleScheduleRecognitionCreateComplete = async (createdScheduledRecognition: SchedulerEvent) => {
    onRecognitionCreated({
      recognition: createdScheduledRecognition,
      rewardPoints: rewardInfo.pointsPerUser || 0,
      isScheduled: true,
      msgTemplateType: selectedMessageTemplate.template?.name,
      msgTemplateKind: selectedMessageTemplate.isEdited ? "edited" : "default",
      source: isFirstRecognition ? "celebration_onboarding" : ""
    })
    return onProcessComplete(createdScheduledRecognition, true)
  }
  const handleScheduleRecognitionUpdateComplete = async (updatedScheduledRecognition: SchedulerEvent) => {
    onProcessComplete(updatedScheduledRecognition, true)
  }
  const handleRecognitionCreationComplete = async (mutationRes: {createRecognition: Recognition}) => {
    if (scheduledRecognition) {
      await RecognitionService.deleteScheduledRecognition({uuid: scheduledRecognition.uuid})
    }
    setSteps({recognition: true, reward: false})
    onRecognitionCreated({
      recognition: mutationRes.createRecognition,
      rewardPoints: rewardInfo.pointsPerUser || 0,
      isScheduled: false,
      msgTemplateType: selectedMessageTemplate.template?.name,
      msgTemplateKind: !selectedMessageTemplate.template ? undefined : selectedMessageTemplate.isEdited ? "edited" : "default",
      source: isFirstRecognition ? "celebration_onboarding" : undefined
    })
    if (!rewardInfo.pointsPerUser) {
      return onProcessComplete(mutationRes.createRecognition)
    }
    setRecognitionCreated(mutationRes.createRecognition)
    executeReward(mutationRes.createRecognition)
  }
  const onProcessReward = () => {
    executeReward(recognitionCreated!)
  }
  const handleMFARetry = () => setMfaLoad(mfaService.hasFlag())

  const executeReward = async (recognitionToReward: Recognition) => {
    setError()
    if (!rewardInfo.pointsPerUser) {
      return recognitionToReward!
    }
    const mfaHasFlag = await mfaService.hasFlag()
    if (!mfaHasFlag) {
      handleMFARetry()
      return recognitionToReward!
    }
    const destinationUserIds = recognitionToReward!.receivers?.results.map(({uuid}) => uuid)

    const isUnassignedBudget = rewardInfo?.budgetId === data?.budgets?.unassigned.uuid
    const budgetDetails =
      isUnassignedBudget || !rewardInfo?.budgetId
        ? null
        : {
            categoryId: rewardInfo.budgetId,
            useUnassigned: rewardInfo.useUnassigned
          }
    const rewardPromise = attachRewardToRecognition(
      destinationUserIds,
      rewardInfo.pointsPerUser,
      recognitionToReward,
      FeedEntityType.Recognition,
      budgetDetails
    )
      .then((rewardRes) => {
        if (!rewardRes) {
          return recognitionToReward
        }
        tracking.rewardCreatedOnRecognition({
          type: "recognition"
        })
        setSteps({recognition: true, reward: true})
        setTimeout(() => {
          refetchBudgets()
          // This call is gonna update the cached points for Reward Wallet. We put a timeout because sometimes the wallet is not coming back as updated.
          walletService.get(authentication.user().uuid!, PaymentCurrencyType.Rp, WalletEntityType.Reward, {fetchPolicy: "network-only"})
        }, 2000)
        onProcessComplete(rewardRes)
        return rewardRes
      })
      .catch((e) => {
        setError(e?.networkError?.errorCode || e.message)
        onErrorReward()
      })
      .finally(setSubmit)
    setSubmit(rewardPromise)
  }
  const handleRecognitionError = (response: any) => {
    tracking.errorsRecognition(response)
  }

  const handleUserChange = (_, users) => {
    onRecognitionChange({
      ...recognition,
      userIds: users || []
    })
  }

  const onChange = (editedRecognition: Recognition, key: string, value: any) => {
    let newRecognition = {
      ...editedRecognition,
      [key]: value
    }

    const newRecCanBeSharedWithTeams = isShareableWithTeams(user, newRecognition)

    if (!newRecCanBeSharedWithTeams && newRecognition.share.value === TEAMS) {
      newRecognition = {
        ...newRecognition,
        share: PRIVATE_OPTION(formatMessage)
      }
    }
    onRecognitionChange(newRecognition)
  }
  const onScheduledTimeChange = (date: string, timezone: string) => {
    setScheduledOptions({date, timezone})
  }
  const handleSubmitScheduledRecognition = () => {
    let submitPromise
    const isUnassignedBudget = rewardInfo?.budgetId === data?.budgets?.unassigned.uuid
    const budgetDetails =
      isUnassignedBudget || !rewardInfo?.budgetId
        ? null
        : {
            categoryId: rewardInfo.budgetId,
            useUnassigned: rewardInfo.useUnassigned
          }
    const scheduledRecognitionInput = parseScheduledRecognition(recognition, rewardInfo.pointsPerUser, scheduledOptions, budgetDetails)
    if (scheduledRecognition) {
      submitPromise = RecognitionService.updateScheduledRecognition({
        recognition: {uuid: scheduledRecognition?.uuid!, ...scheduledRecognitionInput}
      }).then(handleScheduleRecognitionUpdateComplete)
    } else {
      submitPromise = RecognitionService.createScheduledRecognition({recognition: scheduledRecognitionInput}).then(
        handleScheduleRecognitionCreateComplete
      )
    }
    submitPromise = submitPromise.catch(handleRecognitionError).finally(() => setSubmit())
    setSubmit(submitPromise)
  }

  const onMessageChange = (messageChange: OnChangeProps) => {
    const newRecognition = {
      ...recognition,
      message: messageChange.message,
      occasionId: messageChange.occasion?.uuid,
      companyValues: messageChange.companyValues?.map(({uuid}) => uuid)
    }
    setSelectedMessageTemplate({
      ...selectedMessageTemplate,
      isEdited: selectedMessageTemplate.template?.text !== messageChange.message
    })
    onRecognitionChange(newRecognition)
  }
  const onSelectedMessageTemplate = (template: RecognitionMessageTemplate) => {
    setSelectedMessageTemplate({
      template,
      isEdited: false
    })
  }
  const onRewardInfoChange = (newRewardInfo: RewardInfo) => {
    setRewardInfo(newRewardInfo)
    onRecognitionChange({
      ...recognition,
      customRewardId: newRewardInfo.customRewardId
    })
  }
  const recipientsLimitReached = recognition.userIds.length > MAXIMUM_REWARD_RECIPIENTS
  const hintKeyRecipients =
    recognition.userIds.length > 0 ? "platform.recipients.list.recipientsHint" : "platform.recipients.list.recipientsHintDefault"
  return (
    <Loading load={mfaLoad} Error={() => <YouNeedToMfa onRetry={handleMFARetry} hasShadow={false} />}>
      <Spacing>
        {isFirstRecognition && (
          <div>
            <H3>
              <FormattedMessage id="recognition.popup.createRecognition.create.firstRecognition.header" />
            </H3>
            <FirstRecognitionHeaderBody>
              <Body weight="bold" small>
                <FormattedMessage id="recognition.popup.createRecognition.create.firstRecognition.msg.part1" />
                <Body display="inline" small>
                  <FormattedMessage id="recognition.popup.createRecognition.create.firstRecognition.msg.part2" />
                </Body>
              </Body>
            </FirstRecognitionHeaderBody>
          </div>
        )}
        {!isFirstRecognition && (
          <H3>
            <FormattedMessage id="recognition.popup.createRecognition.create.header" />
          </H3>
        )}
        <StyledForm>
          <UserSearch
            hideLabel={!isFirstRecognition}
            onChange={handleUserChange}
            removeSelf
            value={recognition.userIds}
            isMulti
            autoFocus
            validation={Validation.string(formatMessage({id: "platform.defaultValidation.required"})).test(
              "Max 30 recipients",
              formatMessage({id: "platform.recipients.list.maxRecipientsMsg"}),
              () => !recipientsLimitReached
            )}
            hint={
              !recipientsLimitReached && (
                <FormattedMessage
                  id={hintKeyRecipients}
                  values={{count: MAXIMUM_REWARD_RECIPIENTS - recognition.userIds.length, extra: ""}}
                />
              )
            }
            label="recognition.fields.recognition.winners.firstRecognition.label"
            placeholder={
              isFirstRecognition
                ? "recognition.fields.recognition.winners.firstRecognition.placeholder"
                : "recognition.fields.recognition.winners.placeholder"
            }
          />
          <Message
            hideLabel={!isFirstRecognition}
            label={formatMessage({id: "recognition.popup.createRecognition.create.message.label"})}
            placeholder={formatMessage({id: "recognition.popup.createRecognition.create.message.placeholder"})}
            onChange={onMessageChange}
            defaultValues={{
              message: scheduledRecognition?.payload?.publishRecognition?.message,
              occasion: scheduledRecognition?.payload?.publishRecognition?.occasion,
              companyValues: scheduledRecognition?.payload?.publishRecognition?.companyValues
            }}
            hideExtraActions={isFirstRecognition}
            onTemplateSelected={onSelectedMessageTemplate}
            isFirstRecognition={isFirstRecognition}
          />
          <Share
            options={sharingOptions}
            poll={recognition}
            onChange={onChange}
            hasTeams={canBeSharedWithTeams}
            hideLabel
            flush
            components={{
              IndicatorContainer: ({children}) => children,
              Control: ({children, innerProps}) => (
                <Flex {...innerProps} alignItems="center" width="fit-content">
                  <Icon icon={IconTypes.eye} marginRight />
                  {children}
                </Flex>
              ),
              ValueContainer: ({children}) => children
            }}
          />
          {canReward && !isFirstRecognition && (
            <Spacing marginTop={3}>
              <RewardRecognitionBanner
                defaultSettings={rewardInfo}
                recipients={recognition?.receivers?.results || recognition.userIds}
                onChange={onRewardInfoChange}
              />
            </Spacing>
          )}
          {!isFirstRecognition && (
            <Spacing marginTop={2}>
              <Scheduler
                onChange={onScheduledTimeChange}
                defaultValue={scheduledOptions}
                recognition={recognition}
                onScheduleOptionChange={setIsScheduledRecognition}
              />
            </Spacing>
          )}
        </StyledForm>
        {!isScheduledRecognition && (
          <CreateRecognitionMutation
            onCompleted={handleRecognitionCreationComplete}
            onError={handleRecognitionError}
            recognition={recognition}>
            {(onClick, apolloState) => (
              <CTA
                id={getId("submitButton", props)}
                onNext={steps.recognition ? onProcessReward : onClick}
                onCancel={handleCancel}
                apolloState={{...apolloState, loading: apolloState.loading || submit}}
                nextText={formatMessage(
                  {
                    id: isFirstRecognition
                      ? "recognition.popup.createRecognition.createFirstRecognition.cta"
                      : "recognition.popup.createRecognition.create.cta"
                  },
                  {label: selectedMessageTemplate.template?.name.toLowerCase()}
                )}
              />
            )}
          </CreateRecognitionMutation>
        )}
        {isScheduledRecognition && (
          <ValidationButton load={submit} primary full id={getId("submitButton", props)} onClick={handleSubmitScheduledRecognition}>
            <FormattedMessage id="recognition.create.scheduleRecognition.cta" />
          </ValidationButton>
        )}
        {error && (
          <Spacing marginTop={2}>
            <Error errorMsg={parseRewardError(intl, error)} />
          </Spacing>
        )}
      </Spacing>
    </Loading>
  )
}
export default injectIntl(CreateRecognition)
